import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { useTranslation } from "react-i18next";
import { Suspense, lazy } from "react";
import AppLoading from "../../components/app-loading/app-loading";
import { ProductCheckoutProceedModel } from "../../models/products/product-checkout-proceed-model";
import AuthService from "../../services/autentication/auth.service";
import { UserAddress } from "../../models/user-address-model";
import Helpers from "../../utils/helpers";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import { Toast } from "primereact/toast";
import { useAppSelector } from "../../redux/hooks";
import { Message } from "primereact/message";
import { ProductCheckoutModel } from "../../models/products/product-checkout-model";
import OrderService from "../../services/order/order.service";
import TokenService from "../../services/autentication/token.service";

const ItemDeleteDialog = lazy(() => import("./item-delete-dialog"));

const ItemsSummary = (props: any) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);
  const toast = useRef<Toast>(null);
  const isAdmin = useAppSelector((state) => state.isAdmin);
  const adminUsersId = useAppSelector(
    (state) => state.adminUser.selectedAdminUserId
  );
  const [availablePoints, setAvailablePoints] = useState<number>(0);
  const [rewardToCurrencyRatio, setRewardToCurrencyRatio] = useState<number>(0);
  const [loadingRewards, setLoadingRewards] = useState<boolean>(false);
  const [showSparkle, setShowSparkle] = useState<boolean>(false);

  // Fetch reward points based on user type
  useEffect(() => {
    const fetchRewardPoints = async () => {
      try {
        setLoadingRewards(true);
        let userId: number | null = null;

        // If admin has selected a user from dropdown, use that
        if (isAdmin && adminUsersId !== 0) {
          userId = adminUsersId;
          console.log("Admin selected user ID:", userId);
        }
        // Otherwise, get the logged-in user's ID (works for both admin and customer)
        else if (AuthService.isUserLoggedIn()) {
          const userDetails = TokenService.getUser();
          console.log("User details from token:", userDetails);
          userId = userDetails?.userID || null;  // Changed from userId to userID
          console.log("Extracted userId:", userId);
        }

        console.log("Final userId to fetch rewards:", userId);

        if (userId) {
          console.log("Calling API with userId:", userId);
          const response = await OrderService.getUserRewardBalance(userId);
          console.log("API Response:", response);
          setAvailablePoints(response?.availablePoints || 0);
          setRewardToCurrencyRatio(response?.rewardToCurrencyRatio || 0);

          // Trigger sparkle animation for 3 seconds
          setShowSparkle(true);
          setTimeout(() => {
            setShowSparkle(false);
          }, 3000);
        } else {
          console.log("No userId found, setting rewards to 0");
          setAvailablePoints(0);
          setRewardToCurrencyRatio(0);
        }
      } catch (error) {
        console.error("Error fetching reward points:", error);
        setAvailablePoints(0);
        setRewardToCurrencyRatio(0);
      } finally {
        setLoadingRewards(false);
      }
    };

    fetchRewardPoints();
  }, [isAdmin, adminUsersId]);

  const checkInvalidProductCombination = () => {
    const productList = ProductStorageHelpers.GetProductStorage();

    // Check if there are any TIN products (isHomeDeliveryAvailable = false)
    const hasTinProducts = productList.some(
      (item: ProductCheckoutModel) => item.isHomeDeliveryAvailable === false
    );

    // Check if there are any courier unavailable products
    const hasCourierUnavailable = productList.some(
      (item: ProductCheckoutModel) => item.courierAvailable === false
    );

    // Check if any courier unavailable products don't have home delivery available
    const hasCourierUnavailableWithoutHomeDelivery = productList.some(
      (item: ProductCheckoutModel) =>
        item.courierAvailable === false &&
        item.isHomeDeliveryAvailable === false
    );

    // Invalid combination exists only if:
    // 1. There are TIN products (no home delivery) AND courier unavailable products
    // OR
    // 2. There are courier unavailable products that don't have home delivery option
    return (hasTinProducts && hasCourierUnavailable) || hasCourierUnavailableWithoutHomeDelivery;
  };

  const proceedToCheckOut = () => {
    if (checkInvalidProductCombination()) {
      toast.current?.show({
        severity: 'error',
        summary: 'Delivery Not Possible',
        detail: 'Cannot proceed with mixed shippable and non-shippable items',
        life: 5000
      });
      return;
    }

    const storedUserId = ProductStorageHelpers.GetUserId();
    const isAdminUserSelected = adminUsersId !== 0;
    if (isAdmin && !isAdminUserSelected) {
      toast.current?.show({
        severity: 'info',
        summary: 'Warning',
        detail: 'Please select a User before proceeding to checkout',
        life: 3000
      });
      return;
    }

    setNotifyHeader("Checkout Item(s)");
    setNotifyMessage("Do you want to checkout the cart item(s)?");
    setIsNotifyVisible(true);
  };

  const hideNotifyDialog = (value: boolean) => {
    saveProductProcessedData();
    if (value) {
      if (AuthService.isUserLoggedIn()) {
        navigate(RouteConstant.userAddresses);
      } else {
        navigate(RouteConstant.login);
      }
    }
    setIsNotifyVisible(false);
  };

  const saveProductProcessedData = () => {
    const addressData: UserAddress = {
      userAddressID: 0,
      address1: "",
      address2: "",
      landmark: "",
      areaName: "",
      stateName: "",
      subDisticName: "",
      countryName: "",
      zipcode: "",
      gpsLocation: "",
      isPrimary: false,
      homeDeliveryAvailable: false,
      areaId: 0,
      cityName: "",
    };

    const data: ProductCheckoutProceedModel = {
      id: generateUniqueId(),
      totalItemCost: props.total,
      totalTax: props.totalTax,
      deliveryHandlingCharges: 0,
      grandItemTotal: props.total + props.totalTax,
      items: props.orderItems,
      address: addressData,
      paymentOption: {
        id: generateUniqueId(),
        mode: "",
        code: "",
        status: "Not Done",
        description: "",
        dateTime: Date.now.toString(),
      },
      comment: "",
      courierProviderID: 0,
      courierCharges: 0,
      packingCharges: 0,
      handlingCharges: 0,
      paymentGatewayCharges: 0,
    };

    ProductStorageHelpers.SetProcessedProductStorage(data);
  };

  const generateUniqueId = (): string => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 10000);
    return `${timestamp}-${random}`;
  };

  const isCheckoutDisabled = checkInvalidProductCombination();

  const footer = (
    <span>
      <Divider className="my-1" />
      {isCheckoutDisabled ? (
        <div className="p-mb-2">
          <Message
            severity="error"
            text="Combination of shippable and non-shippable products cannot be delivered together. Please create separate orders."
          />
        </div>
      ) : null}
      <Button
        label={t("basket_proceed_to_checkout")}
        onClick={() => proceedToCheckOut()}
        className="checkout-btn font-bold"
        icon="pi pi-check-square"
        raised
        disabled={isCheckoutDisabled}
      />
    </span>
  );

  return (
    <>
      <Toast ref={toast} />
      <Suspense fallback={<AppLoading />}>
        <Card
          title={t("basket_basket_summary")}
          subTitle=""
          footer={footer}
          className="border-1 surface-border box-shadow-none item-summary-content"
        >
          <Divider className="mt-0" />
          <div>
            <div className="flex justify-content-between">
              <p
                className="text-lg font-semibold text-900 mb-1"
                style={{ lineHeight: "1.5" }}
              >
                {t("basket_total_items_cost")}
                <span> :</span>
              </p>
              <p className="text-lg font-semibold text-green">
                {Helpers.formatAmountInINR(props.total)}
              </p>
            </div>
            <div className="flex justify-content-between">
              <p
                className="text-lg font-semibold text-900 mb-1"
                style={{ lineHeight: "1.5" }}
              >
                {t("basket_tax")}
                <span>:</span>
              </p>
              <p className="text-lg font-semibold text-green">
                {Helpers.formatAmountInINR(props.totalTax)}
              </p>
            </div>
            <div className="flex justify-content-between">
              <p
                className="text-lg font-semibold text-900 mb-1"
                style={{ lineHeight: "1.5" }}
              >
                {t("basket_total")}
                <span> :</span>
              </p>
              <p className="text-lg font-semibold text-green">
                {Helpers.formatAmountInINR(Math.round(props.total + props.totalTax))}
              </p>
            </div>

            {/* Rewards Preview - What you could save */}
            {!loadingRewards && availablePoints > 0 && (
              <div className="rewards-preview-section">
                {(() => {
                  const cartTotal = Math.round(props.total + props.totalTax);
                  const rewardsValue = availablePoints * rewardToCurrencyRatio;
                  const applicableRewards = Math.min(rewardsValue, cartTotal);
                  const finalTotal = Math.max(0, cartTotal - applicableRewards);
                  const pointsToUse = Math.ceil(applicableRewards / (rewardToCurrencyRatio > 0 ? rewardToCurrencyRatio : 1));

                  return (
                    <>
                      <div className="flex justify-content-between align-items-center">
                        <p className="text-sm font-semibold mb-0" style={{ color: '#667eea' }}>
                          <i className="pi pi-star-fill mr-2"></i>
                          Use <span className="font-bold text-900">{pointsToUse}</span> Reward Points
                        </p>
                        {/* <p className="text-sm font-bold mb-0" style={{ color: '#48bb78' }}>
                          -{Helpers.formatAmountInINR(applicableRewards)}
                        </p> */}
                      </div>
                      <div className="flex justify-content-between align-items-center mt-2" style={{
                        borderTop: '2px dashed #e1e8ed',
                        paddingTop: '0.5rem'
                      }}>
                        <p className="text-base font-bold text-900 mb-0">
                          Pay Just
                        </p>
                        <div className="flex align-items-center gap-2">
                          {finalTotal !== cartTotal && (
                            <span className="text-sm text-500" style={{ textDecoration: 'line-through' }}>
                              {Helpers.formatAmountInINR(cartTotal)}
                            </span>
                          )}
                          <p className="text-base font-bold mb-0" style={{
                            color: '#48bb78',
                            fontSize: '1.25rem'
                          }}>
                            {Helpers.formatAmountInINR(finalTotal)}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs mb-0 mt-2" style={{ color: '#677688ff', fontStyle: 'italic', textAlign: 'center' }}>
                        <i className="pi pi-check-circle mr-1" style={{ fontSize: '0.7rem' }}></i>
                        Applied automatically at checkout
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </Card>

        <ItemDeleteDialog
          messageHeader={notifyHeader}
          notifyMessage={notifyMessage}
          isVisible={isNotifyVisible}
          isHide={hideNotifyDialog}
        />
      </Suspense>
    </>
  );
};

export default ItemsSummary;